import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const MENU_MAX_HEIGHT = 320;
const MENU_GAP = 6;

/**
 * Single-value picker replacing the native <select>, whose menu the OS draws
 * and whose styling therefore ignores the theme entirely. Same semantics: one
 * value, keyboard reachable, closes on outside click or Escape.
 *
 * The menu renders into document.body through a portal because this control is
 * used inside a Bootstrap modal-dialog-scrollable, whose body sets overflow-y
 * and would otherwise clip an absolutely positioned menu instead of letting it
 * scroll within itself.
 *
 * options: [{ value, label, hint, badge, avatarText, avatarColor }] — hint is a
 * second line under the label, badge a pill on the right, and avatarText an
 * initials circle on the left. Only value and label are required.
 */
const ThemedSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  ariaLabel,
  enableSearch = false,
  searchPlaceholder = 'Search...',
  menuClassName = '',
  flipMenuUpwards = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Height is capped on the inner list, not the menu: the menu keeps its border
  // and radius and clips the list, so the scrollbar sits inside the rounded edge
  // instead of riding on top of it.
  const [listMaxHeight, setListMaxHeight] = useState(MENU_MAX_HEIGHT);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchFocusedRef = useRef(false);
  const selected = options.find((option) => option.value === value);
  useEffect(() => {
    if (!enableSearch) {
      setDebouncedQuery('');
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [enableSearch, searchQuery]);
  const visibleOptions = enableSearch
    ? options.filter((option) => {
        const parts = [option.label, option.hint, option.badge];
        const query = debouncedQuery.trim().toLowerCase();
        if (!query) return true;
        return parts.some((part) => String(part || '').toLowerCase().includes(query));
      })
    : options;

  // Fixed positioning, so the menu is measured against the viewport rather than
  // any scrolling ancestor. Flips above the trigger when there is more room there.
  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const desiredMenuHeight = MENU_MAX_HEIGHT + (enableSearch ? 64 : 12);
    const openUpwards = flipMenuUpwards && spaceBelow < desiredMenuHeight && spaceAbove > spaceBelow;
    const available = Math.max(120, Math.floor(openUpwards ? spaceAbove : spaceBelow));

    setMenuStyle({
      position: 'fixed',
      left: `${Math.round(rect.left)}px`,
      minWidth: `${Math.round(rect.width)}px`,
      maxWidth: `${Math.max(
        Math.round(rect.width),
        Math.round(window.innerWidth - rect.left - 8)
      )}px`,
      ...(openUpwards
        ? { bottom: `${Math.round(window.innerHeight - rect.top + MENU_GAP)}px` }
        : { top: `${Math.round(rect.bottom + MENU_GAP)}px` }),
    });
    // Menu padding (6px top + bottom) sits outside the scrolling list.
    setListMaxHeight(Math.min(MENU_MAX_HEIGHT, available - 12));
  }, [enableSearch, flipMenuUpwards]);

  useEffect(() => {
    if (!isOpen) return undefined;
    positionMenu();
    setSearchQuery('');
    setDebouncedQuery('');

    const onPointerDown = (event) => {
      const insideTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      const insideMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!insideTrigger && !insideMenu) setIsOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') setIsOpen(false); };
    const onReflow = () => positionMenu();
    // A scroll closes the menu, like a click outside. Following the trigger
    // instead slid the menu over the fixed top bar and off the screen. Capture
    // phase, so any scrolling ancestor counts (a modal body too, not just the
    // window); a scroll inside the menu's own list is just the list scrolling.
    const onScroll = (event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, positionMenu]);

  // autoFocus alone misses the first open: the menu is still visibility:hidden
  // (not positioned yet) when the input mounts, and a hidden element can't take
  // focus. So focus it once the menu has a position — once per open, so a later
  // reposition (scroll, resize) never pulls focus back from an option.
  useEffect(() => {
    if (!isOpen) {
      searchFocusedRef.current = false;
      return;
    }
    if (!enableSearch || !menuStyle || searchFocusedRef.current || !searchInputRef.current) return;
    // preventScroll: a scroll now closes the menu, so focusing must not cause one.
    searchInputRef.current.focus({ preventScroll: true });
    searchFocusedRef.current = true;
  }, [isOpen, enableSearch, menuStyle]);

  // A disabled control must never keep a menu open behind it.
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const menu = (
    <div
      ref={menuRef}
      className={`themed-select__menu ${menuClassName}`.trim()}
      style={menuStyle || { position: 'fixed', visibility: 'hidden' }}
    >
      {enableSearch && (
        <div className="themed-select__search-wrap">
          <i className="bi bi-search themed-select__search-icon" aria-hidden="true"></i>
          <input
            ref={searchInputRef}
            type="text"
            className="themed-select__search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoFocus
          />
        </div>
      )}
      <ul
        className="themed-select__list scrollable-element pe-1"
        role="listbox"
        aria-label={ariaLabel}
        style={{ maxHeight: `${listMaxHeight}px` }}
      >
      {visibleOptions.length === 0 ? (
        <li className="themed-select__empty">
          {searchQuery ? 'No matches found' : 'Nothing to choose from'}
        </li>
      ) : (
        visibleOptions.map((option) => (
          <li key={option.value}>
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`themed-select__option ${option.value === value ? 'is-selected' : ''}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.avatarText && (
                <span
                  className="themed-select__avatar"
                  style={option.avatarColor ? { backgroundColor: option.avatarColor } : undefined}
                  aria-hidden="true"
                >
                  {option.avatarText}
                </span>
              )}
              <span className="themed-select__option-text">
                <span className="themed-select__option-label">{option.label}</span>
                {option.hint && <small>{option.hint}</small>}
              </span>
              {option.badge && <span className="themed-select__badge">{option.badge}</span>}
              {option.value === value && <i className="bi bi-check2 themed-select__tick"></i>}
            </button>
          </li>
        ))
      )}
      </ul>
    </div>
  );

  return (
    <div className="themed-select">
      <button
        ref={triggerRef}
        type="button"
        className="themed-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={selected ? '' : 'themed-select__placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <i className={`bi bi-chevron-down themed-select__caret ${isOpen ? 'is-open' : ''}`}></i>
      </button>

      {isOpen && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
};

export default ThemedSelect;
